"use client";

import React, { Ref, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useUpdateCurrentUser } from '../hooks/useUpdateCurrentUser';
import { useLocation } from '../../lib/routeUtil';
import { useCurrentUser } from '../common/withUser';
import { MAX_LOW_KARMA_THRESHOLD } from '../../lib/collections/posts/views'
import { AnalyticsContext, useTracking } from "../../lib/analyticsEvents";
import { getSortOrderOptions } from '../../lib/collections/posts/dropdownOptions';
import { isFriendlyUI } from '../../themes/forumTheme';
import DeferRender from '../common/DeferRender';
import { TooltipRef } from '../common/FMTooltip';
import SingleColumnSection from "../common/SingleColumnSection";
import SectionTitle from "../common/SectionTitle";
import SortButton from "../icons/SortButton";
import SettingsButton from "../icons/SettingsButton";
import PostsListSettings from "./PostsListSettings";
import AllPostsList from "./AllPostsList";
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const styles = defineStyles("AllPostsPage", (theme: ThemeType) => ({
  title: {
    cursor: "pointer",
    "& .SectionTitle-title": {},
  },
  divider: {
    border: "none",
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  settings: {
    display: "grid",
    gridTemplateRows: "0fr",
    // Fade out before collapsing; expand before fading in.
    transition: "grid-template-rows 180ms ease 120ms",
    "@media (prefers-reduced-motion: reduce)": {
      "&, & $settingsContent": {
        transition: "none",
      },
    },
  },
  settingsContent: {
    minHeight: 0,
    overflow: "hidden",
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 120ms ease, visibility 0ms linear 120ms",
  },
  settingsExpanded: {
    gridTemplateRows: "1fr",
    transitionDelay: "0ms",
    "& $settingsContent": {
      opacity: 1,
      visibility: "visible",
      transitionDelay: "180ms, 0ms",
    },
  },
}));

const formatSort = (sorting: PostSortingMode) => {
  const sort = getSortOrderOptions()[sorting].label
  return isFriendlyUI() ? sort : `Sorted by ${sort}`;
}

const AllPostsPage = ({defaultHideSettings}: {defaultHideSettings?: boolean}) => {
  const classes = useStyles(styles);
  const currentUser = useCurrentUser();
  const updateCurrentUser = useUpdateCurrentUser();
  const {query} = useLocation();
  const {captureEvent} = useTracking();

  const [showSettings, setShowSettings] = useState<boolean>(defaultHideSettings ? false : !!currentUser?.allPostsOpenSettings);

  const toggleSettings = useCallback(() => {
    const newValue = !showSettings;
    setShowSettings(newValue);
    captureEvent("toggleSettings", {
      action: newValue,
      listContext: "allPostsPage",
    });
    if (currentUser) {
      void updateCurrentUser({
        allPostsOpenSettings: newValue,
      });
    }
  }, [showSettings, captureEvent, currentUser, updateCurrentUser]);

  const currentTimeframe = query.timeframe || currentUser?.allPostsTimeframe || 'daily';
  const currentSorting = (query.sortedBy   || currentUser?.allPostsSorting   || 'magic') as PostSortingMode;
  const currentFilter = query.filter       || currentUser?.allPostsFilter    || 'all';
  const currentShowLowKarma = (parseInt(query.karmaThreshold) === MAX_LOW_KARMA_THRESHOLD) ||
    currentUser?.allPostsShowLowKarma || false;
  const currentIncludeEvents = (query.includeEvents === 'true') || currentUser?.allPostsIncludeEvents || false;
  return (
    <>
      <AnalyticsContext pageContext="allPostsPage">
        <SingleColumnSection>
        <DeferRender ssr={false}>
          <TooltipRef
            title={`${showSettings ? "Hide": "Show"} options for sorting and filtering`}
            placement="top-end"
          >
            {(ref: Ref<HTMLDivElement>) => <div ref={ref} className={classes.title} onClick={toggleSettings}>
              <SectionTitle title={"All Posts"}>
                {isFriendlyUI() ?
                  <SortButton label={formatSort(currentSorting)} /> :
                  <SettingsButton label={`Sorted by ${ getSortOrderOptions()[currentSorting].label }`}/>
                }
              </SectionTitle>
            </div>}
          </TooltipRef>
          {isFriendlyUI() && !showSettings && <hr className={classes.divider} />}
          <div
            className={classNames(classes.settings, { [classes.settingsExpanded]: showSettings })}
            inert={!showSettings}
          >
            <div className={classes.settingsContent}>
              <PostsListSettings
                hidden={false}
                currentTimeframe={currentTimeframe}
                currentSorting={currentSorting}
                currentFilter={currentFilter}
                currentShowLowKarma={currentShowLowKarma}
                currentIncludeEvents={currentIncludeEvents}
                persistentSettings
                showTimeframe
              />
            </div>
          </div>
          <AllPostsList
            {...{
              currentTimeframe,
              currentSorting,
              currentFilter,
              currentShowLowKarma,
              currentIncludeEvents,
              showSettings,
            }}
          />
        </DeferRender>
        </SingleColumnSection>
      </AnalyticsContext>
    </>
  );
}

export default AllPostsPage;


